// ============================================================================
// StrengthsOverview (L16, F-831..F-833) — Forces Gallup + Couleurs Nova
// ============================================================================
// Portage de components/profile/strengths-overview.tsx : 2 cartes (liste ordonnee
// 1-5 OU EmptyState « aucune force » ; 4 jauges Nova OU EmptyState « profil non
// renseigne »), + slot `editAction` rendu SOUS la grille (F-833 — fourni
// UNIQUEMENT sur /profile, jamais sur la fiche d'un tiers).
//
// ⚠ NT-L16-03 — ce composant est le siege de la divergence assumee vs le PoC. Sur
// /profile/:email, le PoC le rendait SANS AUCUNE GARDE (F-819) : les traits
// psychometriques de n'importe qui etaient lisibles par tout authentifie. Ici, le
// SERVEUR ne sert forces/Nova d'un TIERS que derriere la garde management
// (canViewMemberStrengths) et renvoie sinon des valeurs vides — le composant rend
// alors naturellement ses EmptyStates. Sur SON PROPRE profil, toujours visibles.
// La garde est donc appliquee A LA SOURCE : ce composant reste presentational.
//
// GaugeBar (PoC components/dashboard/gauge-bar) est HORS LOT -> jauge inline en
// tokens (une div de largeur %) plutot que porter un composant hors manifeste.

import { Palette, Sparkles } from 'lucide-react';
import { EmptyState, GS_COMPONENTS } from '@gs/ui';
import { useTranslation } from '../../i18n';
import { NOVA_COLORS, NOVA_BAR_CLASSES } from '../../lib/profile/nova';
import type { NovaScores } from '../../hooks/useProfile';

function GaugeBar({ value, barClassName }: { value: number | null; barClassName: string }) {
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700">
      <div
        className={`h-full rounded-full transition-all ${barClassName}`}
        style={{ width: `${value ?? 0}%` }}
      />
    </div>
  );
}

function CardShell({ title, description, children }: {
  title: string; description: string; children: React.ReactNode;
}) {
  return (
    <div className={`${GS_COMPONENTS.card} p-4`}>
      <h3 className="text-body font-semibold text-slate-900 dark:text-slate-100">{title}</h3>
      <p className="mt-0.5 text-meta text-slate-500 dark:text-slate-400">{description}</p>
      <div className="mt-3">{children}</div>
    </div>
  );
}

export interface StrengthsOverviewProps {
  strengths: string[];
  nova: NovaScores;
  /** F-833 : panneau d'edition — fourni SEULEMENT sur son propre profil. */
  editAction?: React.ReactNode;
}

export function StrengthsOverview({ strengths, nova, editAction }: StrengthsOverviewProps) {
  const { t } = useTranslation();
  const hasNova = NOVA_COLORS.some((c) => nova[c] !== null);

  return (
    <div className="flex flex-col gap-4">
      <div className="grid items-start gap-4 lg:grid-cols-2">
        {/* F-831 : 5 forces Gallup, de la plus forte (1) a la cinquieme. */}
        <CardShell
          title={t('gest:profile.strengths.gallupTitle')}
          description={t('gest:profile.strengths.gallupDescription')}
        >
          {strengths.length === 0 ? (
            <EmptyState icon={<Sparkles size={40} aria-hidden="true" />} title={t('gest:profile.strengths.gallupEmpty')} />
          ) : (
            <ol className="flex flex-col">
              {strengths.map((s, i) => (
                <li
                  key={`${s}-${i}`}
                  className="flex items-center gap-3 border-b border-slate-100 py-2.5 last:border-b-0 dark:border-slate-700/60"
                >
                  <span className="inline-flex size-7 shrink-0 items-center justify-center rounded-full bg-slate-100 text-meta font-semibold tabular-nums text-slate-500 dark:bg-slate-700 dark:text-slate-300">
                    {i + 1}
                  </span>
                  <span className="text-field font-medium text-slate-800 dark:text-slate-100">{s}</span>
                </li>
              ))}
            </ol>
          )}
        </CardShell>

        {/* F-832 : 4 jauges Nova (aucune contrainte de somme — M-805). */}
        <CardShell
          title={t('gest:profile.strengths.novaTitle')}
          description={t('gest:profile.strengths.novaDescription')}
        >
          {!hasNova ? (
            <EmptyState icon={<Palette size={40} aria-hidden="true" />} title={t('gest:profile.strengths.novaEmpty')} />
          ) : (
            <ul className="flex flex-col gap-3">
              {NOVA_COLORS.map((color) => {
                const value = nova[color];
                return (
                  <li key={color} className="flex flex-col gap-1">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-field font-medium text-slate-800 dark:text-slate-100">
                        {t(`gest:profile.nova.${color}.label`)}
                        <span className="ml-2 text-meta font-normal text-slate-500 dark:text-slate-400">
                          {t(`gest:profile.nova.${color}.description`)}
                        </span>
                      </span>
                      <span className="text-field tabular-nums text-slate-800 dark:text-slate-100">
                        {value === null ? '—' : t('gest:profile.strengths.percent', { value })}
                      </span>
                    </div>
                    <GaugeBar value={value} barClassName={NOVA_BAR_CLASSES[color]} />
                  </li>
                );
              })}
            </ul>
          )}
        </CardShell>
      </div>
      {editAction}
    </div>
  );
}
